import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Autocomplete, TextField, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material'
import { Visibility } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listAudit, listAuditEntityTypes, type AuditEntry, type AuditAction } from '../../api/admin'

const ACTION_LABEL: Record<AuditAction, string> = {
  Create: 'Creare', Update: 'Modificare', Delete: 'Ștergere', Deactivate: 'Dezactivare',
  Login: 'Autentificare', LoginFailed: 'Autentificare eșuată', Logout: 'Deconectare',
  PermissionChange: 'Schimbare permisiuni', SecurityIncident: 'Incident de securitate',
}
const ACTION_COLOR: Record<AuditAction, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  Create: 'success', Update: 'info', Delete: 'error', Deactivate: 'warning',
  Login: 'default', LoginFailed: 'error', Logout: 'default', PermissionChange: 'warning', SecurityIncident: 'error',
}

function parseChanges(changes: string | null): { field: string; oldValue: unknown; newValue: unknown }[] {
  if (!changes) return []
  try {
    const parsed = JSON.parse(changes) as Record<string, { old: unknown; new: unknown }>
    return Object.entries(parsed).map(([field, v]) => ({ field, oldValue: v.old, newValue: v.new }))
  } catch {
    return []
  }
}

/** FR-ADM-011/012/014/031 — jurnal de audit, doar în citire (append-only). */
export default function AuditPage() {
  const [entityType, setEntityType] = useState<string | null>(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [detailTarget, setDetailTarget] = useState<AuditEntry | null>(null)

  const { data: entityTypes } = useQuery({ queryKey: ['administrare', 'audit', 'tipuri-entitate'], queryFn: listAuditEntityTypes })
  const { data, isLoading } = useQuery({
    queryKey: ['administrare', 'audit', entityType, from, to],
    queryFn: () => listAudit({ entityType: entityType ?? undefined, from: from || undefined, to: to || undefined, take: 500 }),
  })

  const columns = useMemo<GridColDef<AuditEntry>[]>(() => [
    { field: 'occurredAt', headerName: 'Data', width: 170, valueFormatter: (v: string) => new Date(v).toLocaleString('ro-RO') },
    {
      field: 'action', headerName: 'Acțiune', width: 170, sortable: false,
      renderCell: (p) => <Chip size="small" label={ACTION_LABEL[p.row.action]} color={ACTION_COLOR[p.row.action]} variant="outlined" />,
    },
    { field: 'entityType', headerName: 'Tip entitate', width: 160 },
    { field: 'entityId', headerName: 'Id entitate', width: 130, valueGetter: (v: string | null) => v ?? '—' },
    { field: 'userName', headerName: 'Utilizator', width: 160, valueGetter: (v: string | null) => v ?? 'sistem' },
    { field: 'reason', headerName: 'Motiv', flex: 1, minWidth: 200, valueGetter: (v: string | null) => v ?? '' },
    { field: 'ipAddress', headerName: 'IP', width: 130, valueGetter: (v: string | null) => v ?? '' },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => p.row.changes ? (
        <IconButton size="small" title="Vezi modificările" onClick={() => setDetailTarget(p.row)}>
          <Visibility fontSize="small" />
        </IconButton>
      ) : null,
    },
  ], [])

  const changeRows = parseChanges(detailTarget?.changes ?? null)

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Administrare</Typography>
        <Typography variant="inherit" color="text.primary">Audit trail</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Audit trail</Typography>
      <Typography variant="body2" color="text.secondary">
        Jurnal append-only: cine, când, ce s-a schimbat. Nu poate fi modificat sau șters din aplicație.
      </Typography>

      <Stack direction="row" spacing={2}>
        <Autocomplete
          sx={{ width: 260 }} options={entityTypes ?? []} value={entityType} onChange={(_, v) => setEntityType(v)}
          renderInput={(params) => <TextField {...params} label="Filtru tip entitate" />}
        />
        <TextField type="date" label="De la" value={from} onChange={(e) => setFrom(e.target.value)}
          sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField type="date" label="Până la" value={to} onChange={(e) => setTo(e.target.value)}
          sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>

      <Box sx={{ height: 620, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50, 100]} />
      </Box>

      <Dialog open={!!detailTarget} onClose={() => setDetailTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Modificări — {detailTarget?.entityType} {detailTarget?.entityId}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Câmp</TableCell><TableCell>Valoare veche</TableCell><TableCell>Valoare nouă</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {changeRows.map((c) => (
                <TableRow key={c.field}>
                  <TableCell>{c.field}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{c.oldValue === null ? '—' : String(c.oldValue)}</TableCell>
                  <TableCell>{c.newValue === null ? '—' : String(c.newValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDetailTarget(null)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
